import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  Auth,
  SAMLAuthProvider,
  User,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  updateProfile,
} from '@angular/fire/auth';
import { AppUser } from '../models/user.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = inject(Auth);
  private router = inject(Router);

  private _user = signal<AppUser | null>(null);
  readonly user = this._user.asReadonly();

  readonly isLoggedIn = computed(() => this._user() !== null);
  readonly loading = signal(true);

  constructor() {
    onAuthStateChanged(this.auth, async (firebaseUser: User | null) => {
      if (firebaseUser) {
        let displayName = firebaseUser.displayName;

        if (!displayName) {
          const tokenResult = await firebaseUser.getIdTokenResult();
          const firebase = tokenResult.claims['firebase'] as Record<string, unknown> | undefined;
          const attrs = firebase?.['sign_in_attributes'] as Record<string, string> | undefined;

          if (attrs) {
            displayName =
              attrs['displayName'] ??
              this.buildDisplayName(attrs['firstName'], attrs['lastName']);
          }

          if (displayName) {
            await updateProfile(firebaseUser, { displayName });
          }
        }

        this._user.set({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName,
          photoURL: firebaseUser.photoURL,
        });
      } else {
        this._user.set(null);
      }
      this.loading.set(false);
    });
  }

  async loginWithOkta(): Promise<void> {
    const provider = new SAMLAuthProvider(environment.samlProviderId);
    await signInWithPopup(this.auth, provider);
    this.router.navigate(['/dashboard']);
  }

  private buildDisplayName(firstName?: string, lastName?: string): string | null {
    const parts = [firstName, lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
  }

  async logout(): Promise<void> {
    await signOut(this.auth);
    this.router.navigate(['/login']);
  }
}
